import React from 'react';
import { useUserManagement } from './useUserManagement';
import UserList from './UserList';
import UserDetailPanel from './UserDetailPanel';
import UserModals from './UserModals';

export const UserManagementPanel: React.FC = () => {
  const model = useUserManagement();
  const {
    stats, loading, searchQuery, notification, setNotification,
    fetchUsers, handleSearch, inputStyle,
  } = model;

  return (
    <div>
      {/* Notification */}
      {notification && (
        <div className="fixed top-4 right-4 z-50 px-4 py-3 rounded-lg text-sm font-semibold shadow-lg flex items-center gap-2"
          style={{
            backgroundColor: notification.type === 'success' ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
            color: notification.type === 'success' ? 'var(--admin-success)' : 'var(--admin-error)',
            border: `1px solid ${notification.type === 'success' ? 'var(--admin-success)' : 'var(--admin-error)'}`,
          }}
        >
          <span className="flex-1">{notification.message}</span>
          <button onClick={() => { navigator.clipboard.writeText(notification.message); }} className="admin-icon-btn text-xs" aria-label="Copy notification text" style={{ padding: 0, minWidth: 44 }}>Copy</button>
          <button onClick={() => setNotification(null)} className="admin-icon-btn" aria-label="Dismiss notification">×</button>
        </div>
      )}

      {/* Stats Bar */}
      {stats && (
        <div className="rp-bento-card mb-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Total Users', value: stats.total_users, color: 'var(--rp-primary)' },
              { label: 'Suspended', value: stats.suspended_users, color: stats.suspended_users > 0 ? 'var(--admin-error)' : '#6b7280' },
              { label: 'Premium', value: stats.premium_users, color: 'var(--admin-warning)' },
              { label: 'New Today', value: stats.new_today, color: 'var(--admin-success)' },
              { label: 'New This Week', value: stats.new_this_week, color: 'var(--admin-success)' },
              { label: 'Onboarded', value: stats.onboarded, color: 'var(--rp-primary)' },
            ].map(s => (
              <div key={s.label} className="text-center p-3 rounded-lg" style={{ backgroundColor: 'var(--rp-surface-secondary)' }}>
                <div className="text-2xl font-bold" style={{ color: s.color }}>{s.value}</div>
                <div className="text-xs mt-1" style={{ color: 'var(--rp-text-secondary)' }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Search */}
      <div className="rp-bento-card mb-6">
        <div className="flex items-center gap-4">
          <div className="flex-1 relative">
            <label htmlFor="admin-user-search" className="sr-only">Search users</label>
            <input
              id="admin-user-search"
              type="text"
              value={searchQuery}
              onChange={e => handleSearch(e.target.value)}
              placeholder="Search by email, username, display name, or UUID..."
              className="w-full px-4 py-3 rounded-lg text-sm pl-10"
              style={inputStyle}
            />
            <svg className="absolute left-3 top-3.5 w-4 h-4" style={{ color: 'var(--rp-text-tertiary)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <button onClick={() => fetchUsers(searchQuery)} disabled={loading}
            className="admin-btn-secondary px-4 py-3 rounded-lg text-sm disabled:opacity-50"
          >
            {loading ? 'Loading...' : 'Refresh'}
          </button>
        </div>
      </div>

      {/* Two-panel layout: User list + Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <UserList model={model} />
        </div>
        <div className="lg:col-span-2">
          <UserDetailPanel model={model} />
        </div>
      </div>

      <UserModals model={model} />
    </div>
  );
};
